import { Metric } from '../../../dashboard-config/dashboard-config.model';
import { MetricProfile } from '../../../dashboard-config/metrics/metric-profile';
import { companiesCountProfile, companiesRevenueProfile, gvaProfile, employedProfile, economicParticipantsProfile } from '../../../dashboard-config/metrics/metric-profile-implementations';

/** Four-grid / structure popup presentation. */
export interface ComparisonMetricPopupView {
  metricId: Metric;
  kEur?: boolean;
  detailSlot?: 'economic_participants' | 'employed';
}

/**
 * Comparison facts of a metric, shared by four-grid, structure and industries.
 * KPI selection, columns, labels, value display and export formatting come from
 * `base` and the generic KPI pipeline (see the create* helpers below).
 */
export interface ComparisonMetricProfile {
  base: MetricProfile;
  colKey: string;
  headingPrefix: string;
  /** KPI word in the heading. Empty string omits the part. */
  headingMetricValue: (filterValues: Map<string, any>) => string;
  headingTimeValue: (filterValues: Map<string, any>) => string;
  /** Industries heading: strip Σ∑ from sum-sub-market name (taxed). */
  stripSumSigmaFromSumSubMarket?: boolean;
  popup: ComparisonMetricPopupView;
}

export const comparisonCompaniesCountProfile: ComparisonMetricProfile = {
  base: companiesCountProfile,
  colKey: 'year',
  headingPrefix: companiesCountProfile.labels.headingPhrase,
  headingMetricValue: (fv) => companiesCountProfile.formatKpiHeadingLabel(fv),
  headingTimeValue: (fv) => fv.get('years'),
  stripSumSigmaFromSumSubMarket: true,
  popup: { metricId: Metric.CompaniesCount },
};

export const comparisonCompaniesRevenueProfile: ComparisonMetricProfile = {
  base: companiesRevenueProfile,
  colKey: 'year',
  headingPrefix: companiesRevenueProfile.labels.headingPhrase,
  headingMetricValue: (fv) => companiesRevenueProfile.formatKpiHeadingLabel(fv),
  headingTimeValue: (fv) => fv.get('years'),
  popup: { metricId: Metric.CompaniesRevenue },
};

export const comparisonGvaProfile: ComparisonMetricProfile = {
  base: gvaProfile,
  colKey: 'year',
  headingPrefix: '',
  headingMetricValue: (fv) => gvaProfile.formatKpiHeadingLabel(fv),
  headingTimeValue: (fv) => fv.get('years'),
  popup: { metricId: Metric.GrossValueAdded },
};

export const comparisonEconomicParticipantProfile: ComparisonMetricProfile = {
  base: economicParticipantsProfile,
  colKey: 'year',
  headingPrefix: economicParticipantsProfile.labels.headingPhrase,
  headingMetricValue: (fv) => economicParticipantsProfile.formatKpiHeadingLabel(fv),
  headingTimeValue: (fv) => fv.get('years'),
  popup: {
    metricId: Metric.EconomicParticipant,
    detailSlot: 'economic_participants',
  },
};

export const comparisonEmployedProfile: ComparisonMetricProfile = {
  base: employedProfile,
  colKey: 'period_id',
  headingPrefix: employedProfile.labels.headingPhrase,
  headingMetricValue: (fv) => employedProfile.formatKpiHeadingLabel(fv),
  headingTimeValue: (fv) => fv.get('periods'),
  popup: {
    metricId: Metric.Employed,
    detailSlot: 'employed',
  },
};
